import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import type { FieldState, FieldGroupState, LineItemRow } from "../state/types";

/**
 * Standalone detached field panel — renders in its own browser window.
 * Receives state from the main window via BroadcastChannel.
 * Sends field edits back to the main window.
 *
 * R-137 fix (2026-05-23): previously the parent fired its initial state
 * snapshot on the popup's `load` event, but the child's BroadcastChannel
 * subscription only attaches after React effects run. The post regularly
 * arrived before the listener — message lost, panel stuck on the loading
 * view forever, dim-on-dim text read by operators as "completely black".
 * Two-part fix:
 *   1. Child sends a `request_state` message on mount; parent responds.
 *   2. All chrome (loading + populated) uses design-token CSS vars so the
 *      panel respects light/dark theme instead of hard-coded near-black.
 */

const CHANNEL_NAME = "capture-panel";

interface PanelState {
  fields: FieldState[];
  groups: FieldGroupState[];
  activeFieldIndex: number;
  focusMode: boolean;
  lineItems: Record<string, LineItemRow[]>;
  documentId: string | null;
  batchName: string;
  filename: string;
  snippetVisible: boolean;
}

export function DetachedFieldPanel() {
  const [state, setState] = useState<PanelState | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const msg = event.data;
      if (!msg || typeof msg !== "object") return;

      if (msg.type === "state") {
        setState(msg.payload);
      } else if (msg.type === "close") {
        window.close();
      }
    };

    // R-137: actively ask the parent for the current snapshot instead of
    // depending on the parent's `load`-event push (which races the child's
    // channel subscription and is regularly missed).
    channel.postMessage({ type: "request_state" });

    // Notify main window on close.
    const onBeforeUnload = () => {
      channel.postMessage({ type: "panel_closed" });
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      channel.close();
    };
  }, []);

  const sendEdit = useCallback((fieldId: string, value: string) => {
    channelRef.current?.postMessage({ type: "field_edit", fieldId, value });
  }, []);

  const sendAdvance = useCallback((direction: "next" | "prev") => {
    channelRef.current?.postMessage({ type: "advance_field", direction });
  }, []);

  const sendSetActive = useCallback((index: number) => {
    channelRef.current?.postMessage({ type: "set_active", index });
  }, []);

  const sendToggleFocus = useCallback(() => {
    channelRef.current?.postMessage({ type: "toggle_focus" });
  }, []);

  // Auto-focus the active field input when activeFieldIndex changes.
  // MUST run on every render (Rules of Hooks) — the early-return loading
  // branch below used to skip this hook and crash with "Rendered more
  // hooks than during the previous render" the moment real state arrived.
  const activeFieldIndex = state?.activeFieldIndex ?? -1;
  useLayoutEffect(() => {
    if (activeFieldIndex < 0) return;
    const el = document.querySelector<HTMLInputElement>(
      `.detached-field--active .detached-field-input`,
    );
    if (el && document.activeElement !== el) {
      el.focus();
    }
  }, [activeFieldIndex]);

  if (!state) {
    return (
      <>
        <div className="detached-loading">
          <div className="detached-loading-product">Skarn Capture</div>
          <div className="detached-spinner" />
          <p className="detached-loading-text">Waiting for the verification window…</p>
          <p className="detached-loading-hint">
            If this message persists, return to the verification tab and click <strong>Reattach</strong>,
            then <strong>Detach Panel</strong> again.
          </p>
        </div>
        <style>{detachedPanelStyles}</style>
      </>
    );
  }

  const { fields, groups, focusMode } = state;
  const attentionCount = fields.filter((f) => f.needsAttention).length;
  const doneCount = fields.filter(
    (f) => f.validationStatus === "pass" || f.validationStatus === "overridden",
  ).length;

  // Group fields by groupId.
  const fieldsByGroup = new Map<string, number[]>();
  fields.forEach((f, i) => {
    const list = fieldsByGroup.get(f.groupId) ?? [];
    list.push(i);
    fieldsByGroup.set(f.groupId, list);
  });

  return (
    <div className="detached-panel">
      <div className="detached-header">
        <div className="detached-title">
          <span className="detached-product">Capture</span>
          <span className="detached-label">Field Panel</span>
        </div>
        <div className="detached-doc-info">
          <span>{state.batchName}</span>
          <span className="detached-sep">·</span>
          <span>{state.filename}</span>
        </div>
      </div>

      <div className="detached-toolbar">
        <span className="detached-stats">
          {attentionCount > 0 && (
            <span className="detached-attention">{attentionCount} need attention</span>
          )}
          <span className="detached-done">
            {doneCount}/{fields.length}
          </span>
        </span>
        <button
          className={`detached-focus-btn ${focusMode ? "active" : ""}`}
          onClick={sendToggleFocus}
        >
          Focus
        </button>
      </div>

      <div className="detached-fields">
        {groups.map((group) => {
          const groupFields = (fieldsByGroup.get(group.id) ?? []).filter(
            (i) => !focusMode || fields[i]?.needsAttention,
          );

          if (groupFields.length === 0) return null;

          return (
            <div key={group.id} className="detached-group">
              <div className="detached-group-name">{group.name}</div>
              {groupFields.map((fieldIndex) => {
                const field = fields[fieldIndex]!;
                const isActive = fieldIndex === activeFieldIndex;

                return (
                  <div
                    key={field.id}
                    className={`detached-field ${isActive ? "detached-field--active" : ""}`}
                  >
                    <label className="detached-field-label">
                      {field.label}
                      {field.required && <span className="detached-required">*</span>}
                      {field.validationStatus === "fail" && <span className="detached-fail-dot" />}
                      {field.validationStatus === "pass" && <span className="detached-pass-dot" />}
                    </label>
                    <input
                      className={`detached-field-input ${field.validationStatus === "fail" ? "detached-field-input--error" : ""}`}
                      value={field.value}
                      onChange={(e) => sendEdit(field.id, e.target.value)}
                      onFocus={() => sendSetActive(fieldIndex)}
                      onKeyDown={(e) => {
                        if (e.key === "Tab" || (e.key === "Enter" && !e.ctrlKey && !e.shiftKey)) {
                          e.preventDefault();
                          sendAdvance(e.shiftKey ? "prev" : "next");
                        }
                      }}
                    />
                    {field.validationMessage && (
                      <div className="detached-field-error">{field.validationMessage}</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="detached-nav">
        <button className="detached-nav-btn" onClick={() => sendAdvance("prev")}>
          ← Prev
        </button>
        <span className="detached-nav-pos">
          {activeFieldIndex + 1} / {fields.length}
        </span>
        <button className="detached-nav-btn" onClick={() => sendAdvance("next")}>
          Next →
        </button>
      </div>

      <style>{detachedPanelStyles}</style>
    </div>
  );
}

/**
 * All styles route through globals.css design tokens so the panel inherits
 * the operator's light/dark theme. Hard-coded near-black backgrounds were
 * the visible failure mode of R-137 — every surface now resolves via
 * `--color-bg-*` / `--color-text-*` / `--color-primary`.
 */
const detachedPanelStyles = `
  body {
    background: var(--color-bg-primary, #0e0e0e);
    color: var(--color-text-primary, #f0f0f0);
  }

  .detached-panel {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: var(--color-bg-primary, #0e0e0e);
    color: var(--color-text-primary, #f0f0f0);
  }

  .detached-header {
    padding: 8px 12px;
    border-bottom: 1px solid var(--color-border, #2a2a2a);
    background: var(--color-bg-secondary, #131313);
  }
  .detached-title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 2px;
  }
  .detached-product {
    font-weight: 700;
    color: var(--color-primary, #ea580c);
    font-size: 0.8rem;
  }
  .detached-label {
    font-size: 0.8rem;
    color: var(--color-text-secondary, #a3a3a3);
  }
  .detached-doc-info {
    font-size: 0.75rem;
    color: var(--color-text-muted, #737373);
    display: flex;
    gap: 4px;
  }
  .detached-sep { color: var(--color-border, #2a2a2a); }

  .detached-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 12px;
    border-bottom: 1px solid var(--color-border, #2a2a2a);
    background: var(--color-bg-secondary, #131313);
  }
  .detached-stats {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
  }
  .detached-attention { color: var(--color-warning, #FFC107); }
  .detached-done { color: var(--color-text-secondary, #a3a3a3); }
  .detached-focus-btn {
    background: var(--color-bg-tertiary, #1a1a1a);
    border: 1px solid var(--color-border, #2a2a2a);
    color: var(--color-text-secondary, #a3a3a3);
    padding: 2px 10px;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
  }
  .detached-focus-btn.active {
    border-color: var(--color-primary, #ea580c);
    color: var(--color-primary, #ea580c);
  }

  .detached-fields { flex: 1; overflow-y: auto; padding: 8px 12px; }

  .detached-group { margin-bottom: 12px; }
  .detached-group-name {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted, #737373);
    margin-bottom: 6px;
    font-weight: 600;
  }

  .detached-field { margin-bottom: 6px; }
  .detached-field--active {
    background: var(--color-bg-tertiary, #1a1a1a);
    margin: -4px -6px 6px;
    padding: 4px 6px;
    border-radius: 4px;
  }

  .detached-field-label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: var(--color-text-secondary, #a3a3a3);
    margin-bottom: 2px;
  }
  .detached-required { color: var(--color-error, #F44336); }
  .detached-fail-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-error, #F44336);
  }
  .detached-pass-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--color-success, #4CAF50);
  }

  .detached-field-input {
    width: 100%;
    padding: 4px 8px;
    background: var(--color-bg-secondary, #131313);
    border: 1px solid var(--color-border, #2a2a2a);
    border-radius: 3px;
    color: var(--color-text-primary, #f0f0f0);
    font-size: 0.85rem;
    outline: none;
  }
  .detached-field-input:focus { border-color: var(--color-primary, #ea580c); }
  .detached-field-input--error { border-color: var(--color-error, #F44336); }
  .detached-field-error {
    font-size: 0.7rem;
    color: var(--color-error, #F44336);
    margin-top: 2px;
  }

  .detached-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    border-top: 1px solid var(--color-border, #2a2a2a);
    background: var(--color-bg-secondary, #131313);
  }
  .detached-nav-btn {
    background: var(--color-bg-tertiary, #1a1a1a);
    border: 1px solid var(--color-border, #2a2a2a);
    color: var(--color-text-secondary, #a3a3a3);
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: pointer;
  }
  .detached-nav-btn:hover {
    border-color: var(--color-primary, #ea580c);
    color: var(--color-primary, #ea580c);
  }
  .detached-nav-pos {
    font-size: 0.75rem;
    color: var(--color-text-muted, #737373);
  }

  .detached-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    gap: 14px;
    padding: 0 24px;
    text-align: center;
    background: var(--color-bg-primary, #0e0e0e);
    color: var(--color-text-primary, #f0f0f0);
  }
  .detached-loading-product {
    font-weight: 700;
    color: var(--color-primary, #ea580c);
    font-size: 0.9rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .detached-loading-text {
    font-size: 0.95rem;
    color: var(--color-text-primary, #f0f0f0);
    margin: 0;
  }
  .detached-loading-hint {
    font-size: 0.8rem;
    color: var(--color-text-secondary, #a3a3a3);
    max-width: 320px;
    line-height: 1.4;
    margin: 0;
  }
  .detached-spinner {
    width: 28px;
    height: 28px;
    border: 2px solid var(--color-border, #2a2a2a);
    border-top-color: var(--color-primary, #ea580c);
    border-radius: 50%;
    animation: detached-spin 0.8s linear infinite;
  }
  @keyframes detached-spin { to { transform: rotate(360deg); } }
`;
